import { useMemo, useRef, useState, type ComponentProps } from 'react'
import {
  Box,
  Button,
  Grid,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Inbox } from 'lucide-react'
import type { ParentTicket, TicketUpdate } from '@/types/parentTicket'
import type { ParentTicketWithMeta } from '@/store/parentTicket'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import { useStickyPanelMaxHeight } from '@/hooks/useStickyPanelMaxHeight'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useTicketOptions } from '@/hooks/useTicketOptions'
import TicketProperties from '@/components/helpdesk/TicketDetail/TicketProperties'
import TaskBottleneckBanner from '@/components/helpdesk/TicketDetail/tasks/TaskBottleneckBanner'
import WorkProgressWidget from '@/components/helpdesk/TicketDetail/tasks/WorkProgressWidget'
import { computeTaskStats } from '@/components/helpdesk/TicketDetail/tasks/taskUtils'
import AtAGlanceStrip, { slaTileFromDueAt, type GlanceTile } from './AtAGlanceStrip'
import OverviewActivitySnippet from './OverviewActivitySnippet'
import TicketPropertiesPanel from './TicketPropertiesPanel'
import AssignedTeamPanel from './AssignedTeamPanel'
import OverviewQuickLinks from './OverviewQuickLinks'
import RequestOriginDrawer from './RequestOriginDrawer'

type Option = { label: string; value: string }

type SharedTicketPropertiesProps = ComponentProps<typeof TicketProperties>

type Props = {
  ticket: ParentTicket
  meta: ParentTicketWithMeta | undefined
  ticketId: string
  tasks: ChildTicketTask[]
  ticketUpdates: TicketUpdate[]
  statusOptions: Option[]
  supportTypeLabels: Record<string, string>
  supportTypeRaw?: string | null
  storeCode?: string | null
  purchaseOrderDisplay?: string | null
  sharedTicketPropertiesProps: SharedTicketPropertiesProps
  assetsCount: number
  onTabChange: (tab: number) => void
  onStatusSaved: (status: ParentTicket['status']) => void
  onRefreshActivity: () => void | Promise<void>
  onCsatSent?: () => void | Promise<void>
  onFeVisitsCreated?: () => void | Promise<void>
  tabTasks: number
  tabActivity: number
}

function formatSlaDueSubtext(slaDueAt: string | null | undefined): string | undefined {
  if (!slaDueAt) return undefined
  try {
    const formatted = new Date(slaDueAt).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    return `Due ${formatted}`
  } catch {
    return undefined
  }
}

function formatRequestBannerDate(iso?: string | null): string | null {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return null
  }
}
function severityTone(severity: ParentTicket['severity']): GlanceTile['tone'] {
  if (severity == null) return 'default'
  const n = Number(severity)
  if (n >= 4) return 'red'
  if (n >= 3) return 'orange'
  if (n >= 2) return 'blue'
  return 'default'
}

export default function OverviewTabPanel({
  ticket,
  meta,
  ticketId,
  tasks,
  ticketUpdates,
  statusOptions,
  supportTypeLabels,
  supportTypeRaw,
  storeCode,
  purchaseOrderDisplay,
  sharedTicketPropertiesProps,
  assetsCount,
  onTabChange,
  onStatusSaved,
  onRefreshActivity,
  onCsatSent,
  onFeVisitsCreated,
  tabTasks,
  tabActivity,
}: Props) {
  const colors = useThemeColors()
  const { labelMap: severityLabels } = useTicketOptions('severity')
  const railStickyRef = useRef<HTMLDivElement>(null)
  const railMaxHeight = useStickyPanelMaxHeight(railStickyRef)
  const [isRequestDrawerOpen, setIsRequestDrawerOpen] = useState(false)

  const request = sharedTicketPropertiesProps.request
  const taskStats = useMemo(() => computeTaskStats(tasks), [tasks])
  const statusLabel = statusOptions.find((o) => o.value === ticket.status)?.label ?? ticket.status
  const severityLabel =
    ticket.severity != null
      ? severityLabels[String(ticket.severity)] ?? String(ticket.severity)
      : '—'

  const totalTicketTime =
    (meta as ParentTicketWithMeta & { total_ticket_time?: string | null })?.total_ticket_time ?? null

  const glanceTiles = useMemo<GlanceTile[]>(() => {
    const sla = slaTileFromDueAt(ticket.slaDueAt)
    const slaSubValue =
      sla.tone === 'red' && sla.subValue
        ? sla.subValue
        : formatSlaDueSubtext(ticket.slaDueAt) ?? sla.subValue
    return [
      {
        id: 'sla',
        label: 'SLA',
        value: sla.value,
        subValue: slaSubValue,
        tone: sla.tone,
      },
      {
        id: 'status',
        label: 'Status',
        value: statusLabel,
        subValue: ticket.reopenedAt ? 'Reopened' : undefined,
      },
      {
        id: 'severity',
        label: 'Severity',
        value: severityLabel,
        tone: severityTone(ticket.severity),
      },
      {
        id: 'tasks',
        label: 'Tasks',
        value: taskStats.total > 0 ? `${taskStats.completed} of ${taskStats.total} done` : 'No tasks',
        onClick: taskStats.total > 0 ? () => onTabChange(tabTasks) : undefined,
        alertDot: taskStats.breached > 0,
      },
    ]
  }, [ticket.slaDueAt, ticket.reopenedAt, ticket.severity, statusLabel, severityLabel, taskStats, onTabChange, tabTasks])

  const requestChannel = request
    ? [request.requestSource, request.requestChannel].filter(Boolean).join(' · ') || 'request'
    : null

  const requestSubmittedAt = request?.submittedAt ?? request?.requestedAt ?? request?.createdAt
  const requestSubmittedLabel = formatRequestBannerDate(requestSubmittedAt)
  const requestContact = request?.contactName?.trim()
  const requestAttachmentCount = request?.attachmentMetadata?.length ?? 0

  const billable = ticket.billable ?? (meta as ParentTicketWithMeta & { billable?: boolean | null })?.billable
  const poTrigger = ticket.poTrigger ?? (meta as ParentTicketWithMeta & { poTrigger?: boolean | null })?.poTrigger
  const ticketNumber =
    (meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number ??
    sharedTicketPropertiesProps.ticket?.ticketNumber ??
    null

  const quickLinks = useMemo(
    () => [
      {
        id: 'tasks',
        label: 'Tasks',
        count: tasks.length,
        onClick: () => onTabChange(tabTasks),
        alertDot: taskStats.breached > 0,
      },
      {
        id: 'activity',
        label: 'Activity & comments',
        count: ticketUpdates.length,
        onClick: () => onTabChange(tabActivity),
      },
    ],
    [tasks.length, ticketUpdates.length, taskStats.breached, onTabChange, tabTasks, tabActivity],
  )

  return (
    <>
      <TaskBottleneckBanner tasks={tasks} onViewTasks={() => onTabChange(tabTasks)} />

      <AtAGlanceStrip tiles={glanceTiles} />

      {request ? (
        <HStack
          justify="space-between"
          align="center"
          flexWrap="wrap"
          gap={2}
          mb={3.5}
          px={3}
          py={2}
          bg={colors.cardBg}
          borderWidth="1px"
          borderColor={colors.border}
          borderRadius="md"
        >
          <HStack spacing={2} minW={0} flex="1">
            <Box color={colors.textSecondary} flexShrink={0}>
              <Inbox size={14} aria-hidden />
            </Box>
            <Text fontSize="xs" color={colors.textSecondary} noOfLines={2}>
              Originated from request {request.id}
              {requestContact ? ` · submitted by ${requestContact}` : ''}
              {requestChannel ? ` via ${requestChannel}` : ''}
              {requestSubmittedLabel ? ` · ${requestSubmittedLabel}` : ''}
              {requestAttachmentCount > 0
                ? ` · ${requestAttachmentCount} attachment${requestAttachmentCount === 1 ? '' : 's'}`
                : ''}
            </Text>
          </HStack>
          <Button
            size="xs"
            variant="outline"
            flexShrink={0}
            onClick={() => setIsRequestDrawerOpen(true)}
          >
            View request →
          </Button>
        </HStack>
      ) : null}

      <Grid
        w="100%"
        templateColumns={{ base: '1fr', lg: 'minmax(0, 1.7fr) minmax(190px, 1fr)' }}
        columnGap={{ base: 0, lg: 4 }}
        rowGap={{ base: 4, lg: 0 }}
        alignItems="start"
      >
        <Box minW={0} w="100%">
          <VStack align="stretch" spacing={3}>
            <TicketProperties {...sharedTicketPropertiesProps} contentMode="overview" />
            {assetsCount > 0 ? (
              <TicketProperties {...sharedTicketPropertiesProps} contentMode="assets" embedded />
            ) : null}
            <OverviewActivitySnippet updates={ticketUpdates} onViewAll={() => onTabChange(tabActivity)} />
          </VStack>
        </Box>

        <Box
          ref={railStickyRef}
          minW={0}
          w="100%"
          alignSelf={{ base: 'stretch', lg: 'start' }}
          position={{ base: 'relative', lg: 'sticky' }}
          top={{ lg: 0 }}
          zIndex={1}
          maxH={railMaxHeight != null ? `${railMaxHeight}px` : undefined}
          display={{ lg: 'flex' }}
          flexDirection={{ lg: 'column' }}
          overflow={{ lg: 'hidden' }}
        >
          <Box
            w="100%"
            h={{ base: 'auto', lg: '100%' }}
            minH={0}
            flex={{ base: 'none', lg: '1 1 auto' }}
            overflowY={{ base: 'visible', lg: 'auto' }}
            overflowX="hidden"
            sx={{
              overscrollBehavior: { lg: 'contain' },
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': {
                background: 'gray.300',
                borderRadius: '3px',
              },
            }}
          >
            <VStack align="stretch" spacing={3}>
              <TicketPropertiesPanel
                ticketId={ticketId}
                ticket={ticket}
                statusOptions={statusOptions}
                supportTypeLabels={supportTypeLabels}
                supportType={supportTypeRaw}
                source={
                  (meta as ParentTicketWithMeta & { source?: string })?.source ??
                  sharedTicketPropertiesProps.ticket?.source
                }
                storeCode={storeCode}
                purchaseOrderDisplay={purchaseOrderDisplay}
                feSchedules={sharedTicketPropertiesProps.feSchedules}
                totalTicketTime={totalTicketTime}
                locationDetails={sharedTicketPropertiesProps.locationDetails}
                ticketNumber={ticketNumber}
                billable={billable}
                poTrigger={poTrigger}
                onStatusSaved={onStatusSaved}
                onRefreshActivity={onRefreshActivity}
                onCsatSent={onCsatSent}
              />
              <AssignedTeamPanel
                ticketId={ticketId}
                primaryEngineer={meta?.primaryEngineer}
                additionalEngineer={meta?.additionalEngineer ?? meta?.backupEngineer}
                additionalEngineers={meta?.additionalEngineers ?? meta?.backupEngineers}
                feSchedules={sharedTicketPropertiesProps.feSchedules}
                tasks={tasks}
                onManageSchedule={() => onTabChange(tabTasks)}
                onFeVisitsCreated={onFeVisitsCreated}
              />
              <WorkProgressWidget
                tasks={tasks}
                parentTicketId={ticketId}
                onViewTasks={() => onTabChange(tabTasks)}
              />
              <OverviewQuickLinks links={quickLinks} />
            </VStack>
          </Box>
        </Box>
      </Grid>

      {request ? (
        <RequestOriginDrawer
          isOpen={isRequestDrawerOpen}
          onClose={() => setIsRequestDrawerOpen(false)}
          request={request}
          ticketRequestId={ticket.requestId}
        />
      ) : null}
    </>
  )
}
